/**
 * Interactions and transcripts (S08, S09).
 *
 * An interaction is a call that has been *analysed*. Not every call becomes one:
 * a call where the caller never spoke has nothing to analyse, and a call that failed has no
 * transcript at all. That is why this list is not simply "calls" — the page is
 * about what was learned from a conversation, and a row with no conclusions in
 * it would be a row an operator cannot act on.
 */
import { z } from 'zod';

import {
  CALL_REASON_FILTERS,
  CALL_RESOLUTION_FILTERS,
  CallReason,
  CallResolution,
  TranscriptRole,
} from './enums.js';
import { phoneLineSummarySchema } from './lines.js';

export const transcriptTurnSchema = z.object({
  seq: z.number().int().min(0),
  role: TranscriptRole.schema,
  content: z.string(),
  /**
   * Informational only. Consecutive turns routinely share a second, so the
   * order of the list is `seq` and never this.
   */
  spokenAt: z.string().datetime().nullable(),
});
export type TranscriptTurn = z.infer<typeof transcriptTurnSchema>;

/**
 * Sentiment, as the analyzer reports it.
 *
 * Both are plain numbers rather than a formatted string, because the UI colours
 * the score by sign and draws the magnitude as a bar — decisions that need the
 * value, not a rendering of it.
 */
export const sentimentSchema = z.object({
  /** −1.0 (hostile) to +1.0 (delighted). */
  score: z.number().min(-1).max(1),
  /** 0.0 to 1.0: how strongly, regardless of direction. */
  magnitude: z.number().min(0).max(1),
  /** The analyzer's own word for the tone, when it offered one. */
  toneLabel: z.string().nullable(),
});
export type Sentiment = z.infer<typeof sentimentSchema>;

/** One row in the list. */
export const interactionSchema = z.object({
  id: z.string().uuid(),
  /** The line that was dialled. Null if the line has since been removed. */
  line: phoneLineSummarySchema.nullable(),
  /** Who rang, in E.164. Null unless the platform reports it (Q1). */
  callerNumber: z.string().nullable(),
  reason: CallReason.schema,
  resolution: CallResolution.schema,
  /**
   * The caller's word that made this an escalation, when the analyzer did not
   * say so itself (DV4). Null when the resolution is the analyzer's own.
   */
  escalationInferredFrom: z.string().nullable(),
  /**
   * Where the agent forwarded the call, as the platform recorded it
   * (`tel:+359…`). Null unless the call was forwarded.
   */
  transferredTo: z.string().nullable(),
  sentiment: sentimentSchema,
  /** The analyzer's summary. Null when it produced none. */
  summary: z.string().nullable(),
  /** How long the call lasted. Null when the call row has gone or reported none. */
  durationMs: z.number().int().min(0).nullable(),
  /**
   * When the call started, for display.
   *
   * Null when the call row has since been deleted — the interaction survives it
   * (`call_id` is `SET NULL`), because what was learned outlives the record of
   * the attempt.
   */
  callAt: z.string().datetime().nullable(),
  /** When we analysed it. Authoritative for ordering; see the list query. */
  analyzedAt: z.string().datetime(),
});
export type Interaction = z.infer<typeof interactionSchema>;

/** One interaction with everything the drawer needs. */
export const interactionDetailSchema = interactionSchema.extend({
  /**
   * What the caller said their name was, and the number to ring them back on.
   * Shown "as stated by caller": nothing here is verified.
   */
  statedName: z.string().nullable(),
  statedCallback: z.string().nullable(),
  /** Empty when the transcript could not be read. */
  transcript: z.array(transcriptTurnSchema),
  /**
   * Non-empty when the analyzer's output had to be mapped to a safe default —
   * an unrecognised value, a score out of range. Shown so a strange-looking row
   * can be explained rather than doubted.
   */
  mappingWarnings: z.array(z.string()),
});
export type InteractionDetail = z.infer<typeof interactionDetailSchema>;

/** A calendar day, `YYYY-MM-DD`, read in the console's timezone (`APP_TZ`). */
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a date as YYYY-MM-DD');

export const interactionListQuerySchema = z.object({
  /** Matched against the summary, the stated name, the caller's number and the labels. */
  search: z.string().trim().max(120).optional(),
  /** One reason. `UNKNOWN` is not offered: indecision is not a finding. */
  reason: z.enum(CALL_REASON_FILTERS).optional(),
  resolution: z.enum(CALL_RESOLUTION_FILTERS).optional(),
  lineId: z.string().uuid().optional(),
  /** First day included. */
  from: day.optional(),
  /** Last day included. */
  to: day.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(10),
});
export type InteractionListQuery = z.infer<typeof interactionListQuerySchema>;

export const interactionListSchema = z.object({
  items: z.array(interactionSchema),
  total: z.number().int().min(0),
});
export type InteractionList = z.infer<typeof interactionListSchema>;
